
import {cookies, CookKey} from 'utils/cookies';

import {campaignKey, hasCampaign, Touch} from './attribution';
import {uuid} from './util';

// A session ends after this long without activity
export const IDLE_MS = 30 * 60 * 1000;
// Refresh the sliding cookie at most this often
const TOUCH_EVERY_MS = 60 * 1000;

export type Session = {
	id: string
	started: number
	campaign: string
};

let lastTouch = 0;

const read = (): Session | null => {
	const raw = cookies.get(CookKey.ObserverSession);
	if (!raw) {
		return null;
	}
	try {
		const s = JSON.parse(raw) as Session;
		return s.id && s.started ? s : null;
	} catch {
		return null;
	}
};

const write = (s: Session) => {
	cookies.set(CookKey.ObserverSession, JSON.stringify(s), {
		expires: new Date(Date.now() + IDLE_MS),
		sameSite: 'Lax',
	});
	lastTouch = Date.now();
};

// Current session, or a new one when there is none, it went idle (cookie
// expired), or the visitor arrived on a different campaign
export const resolveSession = (touch: Touch): {session: Session, isNew: boolean} => {
	const existing = read();
	const key = campaignKey(touch);
	if (existing && !(hasCampaign(touch) && key !== existing.campaign)) {
		touchSession(existing);
		return {session: existing, isNew: false};
	}
	const session = {id: uuid(), started: Date.now(), campaign: key};
	write(session);
	return {session, isNew: true};
};

// Extend the idle window on activity
export const touchSession = (s: Session) => {
	if (Date.now() - lastTouch > TOUCH_EVERY_MS) {
		write(s);
	}
};

// Is the session still the one in the cookie (not expired / replaced)?
export const isCurrent = (s: Session): boolean => read()?.id === s.id;

export const endSession = () => {
	cookies.remove(CookKey.ObserverSession);
	lastTouch = 0;
};
